<template>
<div class="app-statistics">
	<div class="container">
		<!-- Header -->
		<div class="page-header">
			<div class="row">
				<div class="col-md-9">
					<h1 class="page-title">{{ appName }} {{ $t('Statistic') }}</h1>
				</div>
				<div class="col-md-3">
					<div class="page-header-right text-right"></div>
				</div>
			</div>
		</div>

		<div class="page-content">
			<div class="row mb-3">
				<div class="col-md-12">
					<div class="page-header-right text-right">
						<!-- Date Filter -->
						<daterangepicker
							:presets="filterDatePresets"
							@selected="setFilterDate">
						</daterangepicker>
					</div>
				</div>
			</div>
			<div
				class="p-4 text-center"
				v-if="isFetching">
				<div class="is-loading large dark"></div>
			</div>
			<div class="app-stats" v-if="!isFetching && stats">
				<div class="row">
					<div class="col-md-6 no-gutters">
						<div class="box">
							<div class="box__title"><b>Transaction</b></div>
							<div class="box__content">
								<transaction-chart
									class="chart chart-height"
									:chartId="transactionChartData.chartId"
									:options='transactionChartOptions'
									:chart-data="transactionChartData">
								</transaction-chart>
							</div>
						</div>
						<div class="box">
							<div class="box__title"><b>Commission</b></div>
							<div class="box__content">
								<commission-chart
									class="chart chart-height"
									:chartId="commissionChartData.chartId"
									:options='commissionChartOptions'
									:chart-data="commissionChartData">
								</commission-chart>
							</div>
						</div>
					</div>
					<div class="col-md-6 no-gutters">
						<div class="col-md-12">
							<div class="row">
								<div class="col-md-6">
									<overview
										:text="'Total Orders'"
										:type="'normal'"
										:with-additional="false"
										:icon="'cart'"
										:number="stats.submissions"
										:stats="stats">
									</overview>
								</div>
								<div class="col-md-6">
									<overview
										:text="'Delivered Orders'"
										:type="'normal'"
										:with-additional="false"
										:icon="'check'"
										:icon-type="'uicons'"
										:number="stats.transactions"
										:stats="stats">
									</overview>
								</div>
							</div>
						</div>
						<div class="col-md-12">
							<overview
								:text="'Commission'"
								:type="'normal'"
								:with-additional="false"
								:icon="'rupiah'"
								:number="stats.commissions"
								:stats="stats">
							</overview>
						</div>
						<div class="col-md-12">
							<overview
								:text="'Gross Revenue'"
								:type="'normal'"
								:with-additional="false"
								:icon="'rupiah'"
								:number="stats.gross_revenue"
								:stats="stats">
							</overview>
						</div>
						<div class="col-md-12">
							<overview
								:text="'Shipping Cost'"
								:type="'normal'"
								:with-additional="false"
								:icon="'rupiah'"
								:number="stats.shipping_cost"
								:stats="stats">
							</overview>
						</div>
					</div>
				</div>

				<hr>

				<div class="range-statistic">
					<div class="border-0">
						<div class="row">
							<div class="col-md-12 mb-2">
								<span class="text-bold">User</span>
							</div>
							<div class="col-sm-4 col-md-4">
								<overview
									:text="'Total User'"
									:type="'normal'"
									:with-additional="false"
									:number="totalUser"
									:stats="stats"
									:clickable="true"
									@click="click('adminUser')">
								</overview>
							</div>
							<div class="col-sm-4 col-md-4">
								<overview
									:text="'Supplier'"
									:type="'normal'"
									:with-additional="false"
									:number="stats.users.supplier_count"
									:stats="stats"
									:clickable="true"
									@click="click('adminSupplierReport')">
								</overview>
							</div>
							<div class="col-sm-4 col-md-4">
								<overview
									:text="'Seller'"
									:type="'normal'"
									:with-additional="false"
									:number="stats.users.seller_count"
									:stats="stats"
									:clickable="true"
									@click="click('adminUser')">
								</overview>
							</div>
						</div>

						<product-stats :stats="stats"></product-stats>

					</div>
				</div>

			</div>
		</div>
	</div>
</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import axios from 'axios';
import Overview from '@/components/Overview.vue';
import Daterangepicker from '@/components/Daterangepicker.vue';
import TransactionChart from '@/components/charts/TransactionChart.vue';
import CommissionChart from '@/components/charts/CommissionChart.vue';
import ProductStats from '@/components/stats/ProductStats.vue';
import statsApi from '@/api/statistic';
import { getDateRange, duplicateVar } from '@/lib/helper';
import { statItems } from '@/lib/statics';

const defaultFilterDate = getDateRange();

export default {
	name: 'appStats',
	components: {
		Overview,
		Daterangepicker,
		TransactionChart,
		CommissionChart,
		ProductStats,
	},
	data() {
		return {
			stats: false,
			// isFetching: false,
			filterDate: null,
			source: null,
			fetchCounter: 0,
		};
	},
	methods: {
		...mapActions({
		}),
		fetchData() {
			this.fetchCounter++;

			const dateFormat = 'YYYY-MM-DD';

			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			// Fetch Snapshot Transaction & Commission Statistic
			this.$store.dispatch('fetchDailyStatistic', {
				since: startDate,
				until: endDate,
				product_id: 0,
			});

			const items = statItems.join(',');

			const params = {
				since: startDate,
				until: endDate,
				items,
			};

			const callback = (response) => {
				this.stats = response.data;
				// this.isFetching = false;
				this.fetchCounter--;
			};

			const errorCallback = () => {
				// this.isFetching = false;
				this.fetchCounter--;
			};

			if (this.source) {
				this.source.cancel('Operation canceled by the user.');
			}

			const CancelToken = axios.CancelToken;
			const source = CancelToken.source();
			this.source = source;

			const sourceToken = this.source.token;

			statsApi.getAppStatsNew(params, callback, errorCallback, sourceToken);
		},
		setFilterDate(filterDate) {
			// this.filterDate = filterDate;
			this.$set(this, 'filterDate', filterDate);
			this.fetchData();
		},
		initFilterDate() {
			const filterDate = duplicateVar(defaultFilterDate);
			// filterDate.start = this.moment(this.user.created_at, 'YYYY-MM-DD HH:mm:ss').toDate();
			this.$set(this, 'filterDate', filterDate);
			// this.filterDate = filterDate;
		},
		click(routeName) {
			this.$router.push({ name: routeName });
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			filterDatePresets: 'filterDatePresets',
			appName: 'appName',
			transactionChartData: 'transactionChartData',
			transactionChartOptions: 'transactionChartOptions',
			commissionChartData: 'commissionChartData',
			commissionChartOptions: 'commissionChartOptions',
		}),
		isFetching() {
			return this.fetchCounter > 0;
		},
		totalUser() {
			return this.stats?.users?.seller_count + this.stats?.users?.supplier_count;
		},
	},
	created() {
		this.initFilterDate();
		this.fetchData();
	},
};
</script>

<style scoped>
.chart-height {
	height: 236px;
}
</style>
